import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { DevDocsScmProviderIcon } from '../DevDocsScmProviderIcons.tsx';
import {
	getDevDocsSourceProviderConfigFromValue,
	getDevDocsSourceProviderDisplayName,
} from '../source-resolution/devDocsSourceProviders.ts';

import type { DevDocsBylineConnection } from './devDocsConnections.ts';

const styles = cssMap({
	iconTile: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		boxSizing: 'border-box',
		width: '24px',
		height: '24px',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		backgroundColor: 'transparent',
	},
});

const getIconKey = (providerDisplayName: string | null): string =>
	providerDisplayName?.replace(/([a-z])([A-Z])/g, '$1 $2') ?? 'Repository';

const getProviderIcon = (provider: string | null, providerDisplayName: string) => {
	return {
		key: `devdocs-provider-${getIconKey(providerDisplayName)}`,
		kind: 'element' as const,
		label: providerDisplayName,
		node: <DevDocsScmProviderIcon provider={provider} />,
	};
};

export const getDevDocsProviderIcon = (
	connection: DevDocsBylineConnection | null,
): {
	key: string;
	label: string;
	icon: {
		key: string;
		kind: 'element';
		label: string;
		node: React.JSX.Element;
	};
} | null => {
	if (!connection) {
		return null;
	}

	const providerConfig = getDevDocsSourceProviderConfigFromValue(connection.provider);
	const providerDisplayName = getDevDocsSourceProviderDisplayName(connection.provider);

	if (!providerDisplayName) {
		return null;
	}

	return {
		key: providerConfig ? getIconKey(providerConfig.displayName) : providerDisplayName,
		label: providerDisplayName,
		icon: getProviderIcon(connection.provider, providerDisplayName),
	};
};

export const getDevDocsProviderDisplayName = (connection: DevDocsBylineConnection): string | null =>
	getDevDocsSourceProviderDisplayName(connection.provider);

export const DevDocsProviderIconTile = ({
	connection,
}: {
	connection: DevDocsBylineConnection;
}): React.JSX.Element => {
	return (
		<Box xcss={styles.iconTile} aria-hidden="true">
			<DevDocsScmProviderIcon provider={connection.provider} />
		</Box>
	);
};
